import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import api from '../../services/api';
import { validationMessages } from '../../utils/validationMessages';
import './Workflows.css';

const Workflows = () => {
  const { user } = useSelector((state) => state.auth);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);

  // Form state for new workflow
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    statuses: [
      { key: 'backlog', name: 'Backlog', category: 'open', color: '#808080', is_backlog: true, is_initial: true, is_terminal: false },
      { key: 'in_progress', name: 'In Progress', category: 'in_progress', color: '#2196f3', is_backlog: false, is_initial: false, is_terminal: false },
      { key: 'done', name: 'Done', category: 'done', color: '#4caf50', is_backlog: false, is_initial: false, is_terminal: true },
    ]
  });

  // Form state for new status
  const [newStatus, setNewStatus] = useState({
    key: '',
    name: '',
    description: '',
    category: 'in_progress',
    color: '#808080',
    is_backlog: false,
    is_terminal: false,
    is_initial: false
  });

  // Only Admin and Super Admin can access workflow management
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Inline editing state for status rows
  const [editingName, setEditingName] = useState({ statusId: null, value: '' });
  const [savingStatusId, setSavingStatusId] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  useEffect(() => {
    if (isAdmin) {
      fetchWorkflows();
    }
  }, [isAdmin]);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/workflows/?include_inactive=true');
      setWorkflows(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowDetail = async (workflowId) => {
    try {
      const response = await api.get(`/workflows/${workflowId}`);
      setSelectedWorkflow(response.data);
    } catch (error) {
      toast.error('Failed to load workflow details');
    }
  };

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    if (!newWorkflow.name.trim()) {
      toast.error(validationMessages.workflow.name.required);
      return;
    }
    if (newWorkflow.statuses.length < 1) {
      toast.error(validationMessages.workflow.status.required);
      return;
    }

    // Validate statuses
    const initialStatuses = newWorkflow.statuses.filter(s => s.is_initial);
    const terminalStatuses = newWorkflow.statuses.filter(s => s.is_terminal);
    if (initialStatuses.length !== 1) {
      toast.error(initialStatuses.length === 0
        ? validationMessages.workflow.status.initialRequired
        : 'Workflow must have exactly one initial status');
      return;
    }
    if (terminalStatuses.length !== 1) {
      toast.error(terminalStatuses.length === 0
        ? validationMessages.workflow.status.terminalRequired
        : 'Workflow must have exactly one terminal status');
      return;
    }

    try {
      const payload = {
        name: newWorkflow.name,
        description: newWorkflow.description,
        statuses: newWorkflow.statuses.map((s, idx) => ({
          ...s,
          position: idx
        }))
      };
      await api.post('/workflows/', payload);
      toast.success('Workflow created successfully');
      setShowCreateModal(false);
      setNewWorkflow({
        name: '',
        description: '',
        statuses: [
          { key: 'backlog', name: 'Backlog', category: 'open', color: '#808080', is_backlog: true, is_initial: true, is_terminal: false },
          { key: 'in_progress', name: 'In Progress', category: 'in_progress', color: '#2196f3', is_backlog: false, is_initial: false, is_terminal: false },
          { key: 'done', name: 'Done', category: 'done', color: '#4caf50', is_backlog: false, is_initial: false, is_terminal: true },
        ]
      });
      fetchWorkflows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workflow');
    }
  };

  const handleDeleteWorkflow = (workflowId, workflowName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Workflow',
      message: `Are you sure you want to delete "${workflowName}"? This action cannot be undone.`,
      confirmText: 'Delete Workflow',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/workflows/${workflowId}`);
          toast.success('Workflow deleted');
          fetchWorkflows();
          setSelectedWorkflow(null);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete workflow');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleAddStatus = async (e) => {
    e.preventDefault();
    if (!newStatus.key.trim() || !newStatus.name.trim()) {
      toast.error('Status Key and Status Name are required');
      return;
    }

    try {
      const payload = {
        ...newStatus,
        position: selectedWorkflow.statuses.length
      };
      await api.post(`/workflows/${selectedWorkflow.id}/statuses`, payload);
      toast.success('Status added');
      setShowAddStatusModal(false);
      setNewStatus({
        key: '',
        name: '',
        description: '',
        category: 'in_progress',
        color: '#808080',
        is_backlog: false,
        is_terminal: false,
        is_initial: false
      });
      fetchWorkflowDetail(selectedWorkflow.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add status');
    }
  };

  const handleDeleteStatus = (statusId, statusName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Status',
      message: `Are you sure you want to delete "${statusName}"? Tasks using this status may be affected.`,
      confirmText: 'Delete Status',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/workflows/statuses/${statusId}`);
          toast.success('Status deleted');
          fetchWorkflowDetail(selectedWorkflow.id);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete status');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleStatusReorder = async (result) => {
    if (!result.destination || !selectedWorkflow) return;

    const items = Array.from(selectedWorkflow.statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately
    setSelectedWorkflow({ ...selectedWorkflow, statuses: items });

    try {
      await api.post(`/workflows/${selectedWorkflow.id}/reorder`, {
        status_ids: items.map(s => s.id)
      });
    } catch (error) {
      toast.error('Failed to reorder statuses');
      fetchWorkflowDetail(selectedWorkflow.id);
    }
  };

  const handleSaveStatusField = async (statusId, field, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (field === 'name' && !trimmed) return;
    // Check nothing changed
    const current = selectedWorkflow.statuses.find(s => s.id === statusId);
    if (current && current[field] === trimmed) return;

    setSavingStatusId(statusId);
    // Optimistic update
    setSelectedWorkflow(prev => ({
      ...prev,
      statuses: prev.statuses.map(s => s.id === statusId ? { ...s, [field]: trimmed } : s)
    }));
    try {
      await api.patch(`/workflows/statuses/${statusId}`, { [field]: trimmed });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update status ${field}`);
      // Revert on error
      fetchWorkflowDetail(selectedWorkflow.id);
    } finally {
      setSavingStatusId(null);
    }
  };

  const addStatusToNewWorkflow = () => {
    const newKey = `status_${newWorkflow.statuses.length + 1}`;
    setNewWorkflow({
      ...newWorkflow,
      statuses: [
        ...newWorkflow.statuses,
        {
          key: newKey,
          name: 'New Status',
          category: 'in_progress',
          color: '#808080',
          is_backlog: false,
          is_initial: false,
          is_terminal: false
        }
      ]
    });
  };

  const updateNewWorkflowStatus = (index, field, value) => {
    const updatedStatuses = [...newWorkflow.statuses];
    if (field === 'is_initial' && value === true) {
      // Radio-button behaviour: only one status can be initial
      updatedStatuses.forEach((s, i) => {
        updatedStatuses[i] = { ...updatedStatuses[i], is_initial: i === index };
      });
    } else if (field === 'is_terminal' && value === true) {
      // Radio-button behaviour: only one status can be terminal
      updatedStatuses.forEach((s, i) => {
        updatedStatuses[i] = { ...updatedStatuses[i], is_terminal: i === index };
      });
    } else {
      updatedStatuses[index] = { ...updatedStatuses[index], [field]: value };
    }
    setNewWorkflow({ ...newWorkflow, statuses: updatedStatuses });
  };

  const removeNewWorkflowStatus = (index) => {
    if (newWorkflow.statuses.length <= 2) {
      toast.warning('Workflow must have at least 2 statuses');
      return;
    }
    const updatedStatuses = newWorkflow.statuses.filter((_, i) => i !== index);
    setNewWorkflow({ ...newWorkflow, statuses: updatedStatuses });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'open': return '#808080';
      case 'in_progress': return '#2196f3';
      case 'done': return '#4caf50';
      default: return '#808080';
    }
  };

  if (!isAdmin) {
    return (
      <div className="workflows-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can manage workflows.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workflows-page">
      <div className="page-header">
        <h1>Workflow Management</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create Workflow
        </button>
      </div>

      <div className="workflows-container">
        {/* Workflows List */}
        <div className="workflows-list">
          <h2>Workflows</h2>
          {loading ? (
            <div className="loading">Loading...</div>
          ) : workflows.length === 0 ? (
            <div className="empty-state">No workflows found</div>
          ) : (
            <div className="workflow-items">
              {workflows.map((workflow) => (
                <div
                  key={workflow.id}
                  className={`workflow-item ${selectedWorkflow?.id === workflow.id ? 'selected' : ''}`}
                  onClick={() => fetchWorkflowDetail(workflow.id)}
                >
                  <div className="workflow-info">
                    <span className="workflow-name">
                      {workflow.name}
                      {workflow.is_default && <span className="default-badge">Default</span>}
                    </span>
                    <span className="workflow-meta">
                      {workflow.status_count} statuses
                      {!workflow.is_active && <span className="inactive-badge">Inactive</span>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Workflow Detail */}
        <div className="workflow-detail">
          {selectedWorkflow ? (
            <>
              <div className="detail-header">
                <div>
                  <h2>{selectedWorkflow.name}</h2>
                  {selectedWorkflow.description && (
                    <p className="workflow-description">{selectedWorkflow.description}</p>
                  )}
                  <span className="workflow-projects">
                    Used by {selectedWorkflow.project_count} project(s)
                  </span>
                </div>
                <div className="detail-actions">
                  {!selectedWorkflow.is_default && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteWorkflow(selectedWorkflow.id, selectedWorkflow.name)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              <div className="statuses-section">
                <div className="statuses-header">
                  <h3>Statuses</h3>
                  {!selectedWorkflow.is_default && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowAddStatusModal(true)}
                    >
                      + Add Status
                    </button>
                  )}
                </div>

                <DragDropContext onDragEnd={handleStatusReorder}>
                  <Droppable droppableId="statuses">
                    {(provided) => (
                      <div
                        className="statuses-list"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {selectedWorkflow.statuses.map((status, index) => (
                          <Draggable
                            key={status.id}
                            draggableId={String(status.id)}
                            index={index}
                            isDragDisabled={selectedWorkflow.is_default}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`status-item ${snapshot.isDragging ? 'dragging' : ''}`}
                              >
                                <div className="status-drag-handle">⋮⋮</div>

                                {/* Color swatch — click to pick color */}
                                <label
                                  className="status-color-swatch"
                                  style={{ backgroundColor: status.color }}
                                  title="Click to change color"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="status-color-edit-icon">
                                    <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor">
                                      <path d="M20.71 5.63l-2.34-2.34a1 1 0 00-1.41 0l-3.12 3.12-1.41-1.42-1.42 1.42 1.41 1.41-6.6 6.6A2 2 0 005 16v3h3a2 2 0 001.42-.59l6.6-6.6 1.41 1.42 1.42-1.42-1.42-1.41 3.12-3.12a1 1 0 000-1.65z"/>
                                    </svg>
                                  </span>
                                  <input
                                    type="color"
                                    className="status-color-input-hidden"
                                    value={status.color}
                                    onChange={(e) => handleSaveStatusField(status.id, 'color', e.target.value)}
                                  />
                                </label>

                                <div className="status-info">
                                  {/* Name — click to edit inline */}
                                  {editingName.statusId === status.id ? (
                                    <input
                                      className="status-name-input-inline"
                                      value={editingName.value}
                                      autoFocus
                                      onChange={(e) => setEditingName({ statusId: status.id, value: e.target.value })}
                                      onBlur={() => {
                                        handleSaveStatusField(status.id, 'name', editingName.value);
                                        setEditingName({ statusId: null, value: '' });
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleSaveStatusField(status.id, 'name', editingName.value);
                                          setEditingName({ statusId: null, value: '' });
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingName({ statusId: null, value: '' });
                                        }
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <span
                                      className="status-name status-name-editable"
                                      title="Click to rename"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingName({ statusId: status.id, value: status.name });
                                      }}
                                    >
                                      {savingStatusId === status.id ? (
                                        <span className="status-saving">Saving…</span>
                                      ) : status.name}
                                    </span>
                                  )}
                                  <span className="status-key">{status.key}</span>
                                </div>

                                <div className="status-badges">
                                  <span className={`category-badge ${status.category}`}>
                                    {status.category.replace('_', ' ')}
                                  </span>
                                  {status.is_backlog && (
                                    <span className="flag-badge backlog">Backlog</span>
                                  )}
                                  {status.is_initial && (
                                    <span className="flag-badge initial">Initial</span>
                                  )}
                                  {status.is_terminal && (
                                    <span className="flag-badge terminal">Terminal</span>
                                  )}
                                </div>
                                {!selectedWorkflow.is_default && (
                                  <button
                                    className="btn-icon delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteStatus(status.id, status.name);
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </>
          ) : (
            <div className="empty-detail">
              <p>Select a workflow to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Workflow Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Workflow"
        size="large"
      >
        <form onSubmit={handleCreateWorkflow}>
          <div className="form-group">
            <label className="form-label">Workflow Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              value={newWorkflow.name}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
              placeholder="e.g., Development Workflow"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={newWorkflow.description}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
              placeholder="Describe this workflow..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Statuses</label>
            <div className="new-statuses-list">
              {newWorkflow.statuses.map((status, index) => (
                <div key={index} className="new-status-item">
                  <input
                    type="text"
                    className="form-input status-key-input"
                    value={status.key}
                    onChange={(e) => updateNewWorkflowStatus(index, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="key"
                  />
                  <input
                    type="text"
                    className="form-input status-name-input"
                    value={status.name}
                    onChange={(e) => updateNewWorkflowStatus(index, 'name', e.target.value)}
                    placeholder="Name"
                  />
                  <input
                    type="color"
                    className="color-input"
                    value={status.color}
                    onChange={(e) => updateNewWorkflowStatus(index, 'color', e.target.value)}
                  />
                  <div className="category-select-wrapper">
                    <select
                      className="form-select form-select-sm"
                      value={status.category}
                      onChange={(e) => updateNewWorkflowStatus(index, 'category', e.target.value)}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                    </select>
                  </div>
                  <div className="checkbox-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={status.is_backlog}
                        onChange={(e) => updateNewWorkflowStatus(index, 'is_backlog', e.target.checked)}
                      />
                      Backlog
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={status.is_initial}
                        onChange={(e) => updateNewWorkflowStatus(index, 'is_initial', e.target.checked)}
                      />
                      Initial
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={status.is_terminal}
                        onChange={(e) => updateNewWorkflowStatus(index, 'is_terminal', e.target.checked)}
                      />
                      Terminal
                    </label>
                    <button
                      type="button"
                      className="btn-icon delete"
                      onClick={() => removeNewWorkflowStatus(index)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={addStatusToNewWorkflow}
            >
              + Add Status
            </button>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Workflow
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Status Modal */}
      <Modal
        isOpen={showAddStatusModal}
        onClose={() => setShowAddStatusModal(false)}
        title="Add Status"
      >
        <form onSubmit={handleAddStatus}>
          <div className="form-group">
            <label className="form-label">Status Key <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              value={newStatus.key}
              onChange={(e) => setNewStatus({ ...newStatus, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              placeholder="e.g., in_review"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Display Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              value={newStatus.name}
              onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
              placeholder="e.g., In Review"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={newStatus.category}
                onChange={(e) => setNewStatus({ ...newStatus, category: e.target.value })}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <input
                type="color"
                className="form-input color-input-full"
                value={newStatus.color}
                onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={newStatus.is_backlog}
                onChange={(e) => setNewStatus({ ...newStatus, is_backlog: e.target.checked })}
              />
              Backlog (not shown on board)
            </label>
            <label className="checkbox-label" title={selectedWorkflow?.statuses.some(s => s.is_initial) ? 'Another status is already marked as initial' : ''}>
              <input
                type="checkbox"
                checked={newStatus.is_initial}
                onChange={(e) => setNewStatus({ ...newStatus, is_initial: e.target.checked })}
                disabled={selectedWorkflow?.statuses.some(s => s.is_initial)}
              />
              Initial status (default for new tasks)
              {selectedWorkflow?.statuses.some(s => s.is_initial) && (
                <span className="flag-hint"> — already set on "{selectedWorkflow.statuses.find(s => s.is_initial)?.name}"</span>
              )}
            </label>
            <label className="checkbox-label" title={selectedWorkflow?.statuses.some(s => s.is_terminal) ? 'Another status is already marked as terminal' : ''}>
              <input
                type="checkbox"
                checked={newStatus.is_terminal}
                onChange={(e) => setNewStatus({ ...newStatus, is_terminal: e.target.checked })}
                disabled={selectedWorkflow?.statuses.some(s => s.is_terminal)}
              />
              Terminal status (done-like behavior)
              {selectedWorkflow?.statuses.some(s => s.is_terminal) && (
                <span className="flag-hint"> — already set on "{selectedWorkflow.statuses.find(s => s.is_terminal)?.name}"</span>
              )}
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowAddStatusModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Add Status
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default Workflows;
